import React, { createContext, useState, useContext, useEffect } from 'react';
import { translationsAPI } from '../api/client';
import { useAuth } from './AuthContext';

const TranslationContext = createContext();

export const TranslationProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState({});
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load languages on mount
  useEffect(() => {
    loadLanguages();
  }, []);

  // Load user's preferred language when user changes
  useEffect(() => {
    if (user) {
      const userLanguage = user.languageCode || user.language_code || localStorage.getItem('language') || 'en';
      console.log('User logged in, loading language:', userLanguage);
      loadTranslations(userLanguage);
    } else {
      const savedLanguage = localStorage.getItem('language') || 'en';
      console.log('No user, loading saved language:', savedLanguage);
      loadTranslations(savedLanguage);
    }
  }, [user]);

  const loadLanguages = async () => {
    try {
      const response = await translationsAPI.getLanguages();
      if (response.data.success) {
        setLanguages(response.data.languages);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
    }
  };

  const loadTranslations = async (languageCode) => {
    try {
      setLoading(true);
      const response = await translationsAPI.getTranslations(languageCode);
      
      if (response.data.success) {
        setTranslations(response.data.translations);
        setCurrentLanguage(languageCode);
        localStorage.setItem('language', languageCode);
        console.log('Translations loaded for:', languageCode);
      }
    } catch (error) {
      console.error('Error loading translations:', error);
      // Fallback to English
      if (languageCode !== 'en') {
        loadTranslations('en');
      }
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = async (languageCode) => {
    console.log('Changing language to:', languageCode);
    
    // Update translations immediately
    await loadTranslations(languageCode);
    
    // Update user preference in database if logged in
    if (user) {
      try {
        await translationsAPI.updateUserLanguage(languageCode);
        console.log('User language preference updated in database');
      } catch (error) {
        console.error('Error updating user language preference:', error);
      }
    }
  };

  const t = (key, defaultValue = '') => {
    return translations[key] || defaultValue || key;
  };

  return (
    <TranslationContext.Provider
      value={{
        currentLanguage,
        languages,
        translations,
        loading,
        changeLanguage,
        t
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  return context;
};